import * as React from 'react';
import { NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { Link, PageTitle } from '@/components/common';

const TermsPage: NextPage = () => {
  return <>
      <NextSeo title='Terms of Use' />
      <PageTitle>Terms of Use</PageTitle>
      <div className='space-y-4 mx-auto px-6 max-w-4xl md:px-12'>
        <p>Last Modified: 11/14/2023</p>
        <h3 className='font-bold text-lg'>Acceptance of the Terms of Use</h3>
        <p>
          These terms of use are entered into by and between You and&nbsp;
          <strong>28 Wellness, Inc.&nbsp;</strong>(the “<strong>Company</strong>
          ”, “<strong>we</strong>”, or “<strong>us</strong>”). The following terms and conditions, (collectively these “
          <strong>Terms of Use</strong>”), govern your access to and use of www.28.co, including any content,
          functionality, and services offered on or through www.28.co (the “<strong>Website</strong>”) or the 28 mobile
          application (the “<strong>App</strong>”).
        </p>
        <p>
          Please read the Terms of Use carefully before you start to use the Website or App. By using the Website or
          App, you accept and agree to be bound and abide by these Terms of Use and our{' '}
          <Link className='underline' href='/privacy'>
            Privacy Policy
          </Link>
          , the terms of which are incorporated herein by reference. If you do not want to agree to these Terms of Use
          or the{' '}
          <Link className='underline' href='/privacy'>
            Privacy Policy
          </Link>
          , you must not access or use the Website or App.
        </p>
        <p>
          The Website and the App is offered and available to users who are 18 years of age or older and reside in the
          United States or any of its territories or possessions. By using the Website or the App, or by clicking to
          accept or agree to the Terms of Use when this option is made available to you, you represent and warrant that
          you are of legal age to form a binding contract with the Company and meet all of the foregoing eligibility
          requirements. If you do not meet all of these requirements, you must not access or use Website or App.
        </p>
        <p>
          <strong>Changes to the Terms of Use</strong>
        </p>
        <p>
          We may revise and update these Terms of Use from time to time in our sole discretion. All changes are
          effective immediately when we post them and apply to all access to and use of Website and App thereafter.
        </p>
        <p>
          Your continued use of Website and App following the posting of revised Terms of Use means that you accept and
          agree to the changes. You are expected to check this page from time to time so you are aware of any changes,
          as they are binding on you.
        </p>
        <p>
          <strong>Accessing Website and App and Account Security</strong>
        </p>
        <p>
          We reserve the right to withdraw or amend the Website or the App, and any service or material we provide on
          Website or App, in our sole discretion without notice. We will not be liable if for any reason all or any part
          of Website or App is unavailable at any time or for any period. From time to time, we may restrict access to
          some parts of the Website or App, or the entire Website or App, to users, including registered users.
        </p>
        <p>You are responsible for:</p>
        <p>Making all arrangements necessary for you to have access to Website or App.</p>
        <p>
          Ensuring that all persons who access Website or App through your internet connection are aware of these Terms
          of Use and comply with them.
        </p>
        <p>
          To access Website or App or some of the resources it offers, you may be asked to provide certain registration
          details or other information. It is a condition of your use of Website or App that all the information you
          provide on Website or App is correct, current, and complete. You agree that all information you provide to
          register with the Website or the App or otherwise, including but not limited to through the use of any
          interactive features on Website or App, is governed by, and you consent to all actions we take with respect to
          your information consistent with, our{' '}
          <Link className='underline' href='/privacy'>
            Privacy Policy
          </Link>
          .
        </p>
        <p>
          If you choose, or are provided with, a username, password, or any other piece of information as part of our
          security procedures, you must treat such information as confidential, and you must not disclose it to any
          other person or entity. You also acknowledge that your account is personal to you and agree not to provide any
          other person with access to the Website or the App or portions of it using your username, password, or other
          security information. You agree to notify us immediately of any unauthorized access to or use of your username
          or password or any other breach of security. You also agree to ensure that you exit from your account at the
          end of each session. You should use particular caution when accessing your account from a public or shared
          computer so that others are not able to view or record your password or other personal information.
        </p>
        <p>
          We have the right to disable any username, password, or other identifier, whether chosen by you or provided by
          us, at any time in our sole discretion for any or no reason, including if, in our opinion, you have violated
          any provision of these Terms of Use.
        </p>
        <h3 className='font-bold text-lg'>Intellectual Property Rights</h3>
        <p>
          The Website and App and their entire contents, features, and functionality (including but not limited to all
          information, software, text, displays, images, video, and audio, and the design, selection, and arrangement
          thereof) are owned by the Company, its licensors, or other providers of such material and are protected by
          United States and international copyright, trademark, patent, trade secret, and other intellectual property or
          proprietary rights laws.
        </p>
        <p>
          These Terms of Use permit you to use Website or App for your personal, non-commercial use only. You must not
          reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish,
          download, store, or transmit any of the material on the Website or App, except as follows:
        </p>
        <p>
          Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing
          those materials.
        </p>
        <p>You may store files that are automatically cached by your Web browser for display enhancement purposes.</p>
        <p>
          You may print or download one copy of a reasonable number of pages of Website or App for your own personal,
          non-commercial use and not for further reproduction, publication, or distribution.
        </p>
        <p>
          If we provide desktop, mobile, or other applications for download, you may download a single copy to your
          computer or mobile device solely for your own personal, non-commercial use, provided you agree to be bound by
          our end user license agreement for such applications.
        </p>
        <p>
          If we provide social media features with certain content, you may take such actions as are enabled by such
          features.
        </p>
        <p>You must not:</p>
        <p>Modify copies of any materials from this site.</p>
        <p>
          Use any illustrations, photographs, video or audio sequences, or any graphics separately from the accompanying
          text.
        </p>
        <p>
          Delete or alter any copyright, trademark, or other proprietary rights notices from copies of materials from
          this site.
        </p>
        <p>
          You must not access or use for any commercial purposes any part of Website or App or any services or materials
          available through Website or App.
        </p>
        <p>
          If you wish to make any use of material on Website or App other than that set out in this section, please
          address your request to:&nbsp;
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>
          .
        </p>
        <p>
          If you print, copy, modify, download, or otherwise use or provide any other person with access to any part of
          Website or App in breach of the Terms of Use, your right to use Website and App will stop immediately and you
          must, at our option, return or destroy any copies of the materials you have made. No right, title, or interest
          in or to Website or App or any content on Website or App is transferred to you, and all rights not expressly
          granted are reserved by the Company. Any use of Website or App not expressly permitted by these Terms of Use
          is a breach of these Terms of Use and may violate copyright, trademark, and other laws.
        </p>
        <h3 className='bold'>Trademarks</h3>
        <p>
          The Company name, the terms “28,” the Company logo, and all related names, logos, product and service names,
          designs, and slogans are trademarks of the Company or its affiliates or licensors. You must not use such marks
          without the prior written permission of the Company. All other names, logos, product and service names,
          designs, and slogans on the Website or the App are the trademarks of their respective owners.
        </p>
        <h3 className='bold'>Prohibited Uses</h3>
        <p>
          You may use Website and App only for lawful purposes and in accordance with these Terms of Use. You agree not
          to use Website or App:
        </p>
        <p>
          In any way that violates any applicable federal, state, local, or international law or regulation (including,
          without limitation, any laws regarding the export of data or software to and from the US or other countries).
        </p>
        <p>
          For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way by exposing them to
          inappropriate content, asking for personally identifiable information, or otherwise.
        </p>
        <p>
          To transmit, or procure the sending of, any advertising or promotional material without our prior written
          consent, including any “junk mail”, “chain letter”, “spam”, or any other similar solicitation.
        </p>
        <p>
          To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or
          entity (including, without limitation, by using email addresses or screen names associated with any of the
          foregoing).
        </p>
        <p>
          To engage in any other conduct that restricts or inhibits anyone’s use or enjoyment of Website or App, or
          which, as determined by us, may harm the Company or users of Website or App or expose them to liability.
        </p>
        <p>Additionally, you agree not to:</p>
        <p>
          Use Website or App in any manner that could disable, overburden, damage, or impair the site or interfere with
          any other party’s use of Website or App, including their ability to engage in real time activities through
          Website or App.
        </p>
        <p>
          Use any robot, spider, or other automatic device, process, or means to access Website or App for any purpose,
          including monitoring or copying any of the material on Website or App.
        </p>
        <p>
          Use any manual process to monitor or copy any of the material on Website or App or for any other unauthorized
          purpose without our prior written consent.
        </p>
        <p>Use any device, software, or routine that interferes with the proper working of Website or App.</p>
        <p>
          Introduce any viruses, Trojan horses, worms, logic bombs, or other material that is malicious or
          technologically harmful.
        </p>
        <p>
          Attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of Website or App, the
          server on which Website or App is stored, or any server, computer, or database connected to Website or App.
        </p>
        <p>Attack Website or App via a denial-of-service attack or a distributed denial-of-service attack.</p>
        <p>Otherwise attempt to interfere with the proper working of Website or App.</p>
        <h3 className='font-bold text-lg'>User Contributions</h3>
        <p>
          The Website and App may contain message boards, chat rooms, personal web pages or profiles, forums, bulletin
          boards, and other interactive features (collectively, “<strong>Interactive Services</strong>”) that allow
          users to post, submit, publish, display, or transmit to other users or other persons (hereinafter, “
          <strong>post</strong>”) content or materials (collectively, “<strong>User Contributions</strong>”) on or
          through Website or App.
        </p>
        <p>All User Contributions must comply with the Content Standards set out in these Terms of Use.</p>
        <p>You represent and warrant that:</p>
        <p>
          You own or control all rights in and to the User Contributions and have the right to grant the license granted
          above to us and our affiliates and service providers, and each of their and our respective licensees,
          successors, and assigns.
        </p>
        <p>All of your User Contributions do and will comply with these Terms of Use.</p>
        <p>
          You understand and acknowledge that you are responsible for any User Contributions you submit or contribute,
          and you, not the Company, have full responsibility for such content, including its legality, reliability,
          accuracy, and appropriateness.
        </p>
        <p>
          We are not responsible or liable to any third party for the content or accuracy of any User Contributions
          posted by you or any other user of Website or App.
        </p>
        <h3 className='font-bold text-lg'>Monitoring and Enforcement; Termination</h3>
        <p>Remove or refuse to post any User Contributions for any or no reason in our sole discretion.</p>
        <p>
          Take any action with respect to any User Contribution that we deem necessary or appropriate in our sole
          discretion, including if we believe that such User Contribution violates the Terms of Use, including the
          Content Standards, infringes any intellectual property right or other right of any person or entity, threatens
          the personal safety of users of the Website, App, or the public, or could create liability for the Company.
        </p>
        <p>
          Disclose your identity or other information about you to any third party who claims that material posted by
          you violates their rights, including their intellectual property rights or their right to privacy.
        </p>
        <p>
          Take appropriate legal action, including without limitation, referral to law enforcement, for any illegal or
          unauthorized use of the Website or App.
        </p>
        <p>
          Terminate or suspend your access to all or part of the Website or App for any or no reason, including without
          limitation, any violation of these Terms of Use.
        </p>
        <p>
          Without limiting the foregoing, we have the right to cooperate fully with any law enforcement authorities or
          court order requesting or directing us to disclose the identity or other information of anyone posting any
          materials on or through the Website or App. YOU WAIVE AND HOLD HARMLESS THE COMPANY AND ITS AFFILIATES,
          LICENSEES, AND SERVICE PROVIDERS FROM ANY CLAIMS RESULTING FROM ANY ACTION TAKEN BY THE COMPANY DURING, OR
          TAKEN AS A CONSEQUENCE OF, INVESTIGATIONS BY EITHER THE COMPANY OR LAW ENFORCEMENT AUTHORITIES.
        </p>
        <p>
          However, we do not undertake to review all material before it is posted on the Website or App and cannot
          ensure prompt removal of objectionable material after it has been posted. Accordingly, we assume no liability
          for any action or inaction regarding transmissions, communications, or content provided by any user or third
          party. We have no liability or responsibility to anyone for performance or nonperformance of the activities
          described in this section.
        </p>
        <h3 className='font-bold text-lg'>Content Standards</h3>
        <p>
          These content standards apply to any and all User Contributions and use of Interactive Services. User
          Contributions must in their entirety comply with all applicable federal, state, local, and international laws
          and regulations. Without limiting the foregoing, User Contributions must not:
        </p>
        <p>
          Contain any material that is defamatory, obscene, indecent, abusive, offensive, harassing, violent, hateful,
          inflammatory, or otherwise objectionable.
        </p>
        <p>
          Promote sexually explicit or pornographic material, violence, or discrimination based on race, sex, religion,
          nationality, disability, sexual orientation, or age.
        </p>
        <p>
          Infringe any patent, trademark, trade secret, copyright, or other intellectual property or other rights of any
          other person.
        </p>
        <p>
          Violate the legal rights (including the rights of publicity and privacy) of others or contain any material
          that could give rise to any civil or criminal liability under applicable laws or regulations or that otherwise
          may be in conflict with these Terms of Use and our&nbsp;Privacy Policy.
        </p>
        <p>Be likely to deceive any person.</p>
        <p>Promote any illegal activity, or advocate, promote, or assist any unlawful act.</p>
        <p>
          Cause annoyance, inconvenience, or needless anxiety or be likely to upset, embarrass, alarm, or annoy any
          other person.
        </p>
        <p>Impersonate any person or misrepresent your identity or affiliation with any person or organization.</p>
        <p>
          Involve commercial activities or sales, such as contests, sweepstakes, and other sales promotions, barter, or
          advertising.
        </p>
        <p>
          Give the impression that they emanate from or are endorsed by us or any other person or entity if this is not
          the case.
        </p>
        <h3 className='font-bold text-lg'>Reliance on Information Posted</h3>
        <p>
          The information presented on or through Website and App is made available solely for general information
          purposes. We do not warrant the accuracy, completeness, or usefulness of this information. Any reliance you
          place on such information is strictly at your own risk. We disclaim all liability and responsibility arising
          from any reliance placed on such materials by you or any other visitor to Website or App, or by anyone who may
          be informed of any of its contents.
        </p>
        <p>
          The Website and the App may include content provided by third parties, including materials provided by other
          users, bloggers, and third-party licensors, syndicators, aggregators, and/or reporting services. All
          statements and/or opinions expressed in these materials, and all articles and responses to questions and other
          content, other than the content provided by the Company, are solely the opinions and the responsibility of the
          person or entity providing those materials. These materials do not necessarily reflect the opinion of the
          Company. We are not responsible, or liable to you or any third party, for the content or accuracy of any
          materials provided by any third parties.
        </p>
        <h3 className='font-bold text-lg'>Changes to Website or App</h3>
        <p>
          We may update the content on the Website or the App from time to time, but its content is not necessarily
          complete or up to date. Any of the material on Website or App may be out of date at any given time, and we are
          under no obligation to update such material.
        </p>
        <h3 className='font-bold text-lg'>Information About You and Your Visits to the Website</h3>
        <p>
          All information we collect on the Website or the App is subject to our{' '}
          <Link className='underline' href='/privacy'>
            Privacy Policy
          </Link>
          . By using Website or App, you consent to all actions taken by us with respect to your information in
          compliance with the{' '}
          <Link className='underline' href='/privacy'>
            Privacy Policy
          </Link>
          .
        </p>
        <h3 className='font-bold text-lg'>Other Terms and Conditions</h3>
        <p>
          Additional terms and conditions may also apply to specific portions, services, or features of Website or App.
          All such additional terms and conditions are hereby incorporated by this reference into these Terms of
          Use.&nbsp; Without limiting the foregoing, all purchases through our site or other transactions for the sale
          of goods or services, or resulting from visits made by you, are governed by our terms of sale applicable to
          the sale.
        </p>
        <h3 className='font-bold text-lg'>Linking to Website or App and Social Media Features</h3>
        <p>
          You may link to our homepage, provided you do so in a way that is fair and legal and does not damage our
          reputation or take advantage of it, but you must not establish a link in such a way as to suggest any form of
          association, approval, or endorsement on our part without our express written consent.
        </p>
        <p>The Website and the App may provide certain social media features that enable you to:</p>
        <p>Link from your own or certain third-party websites to certain content on the Website or the App.</p>
        <p>
          Send emails or other communications with certain content, or links to certain content, on the Website or the
          App.
        </p>
        <p>
          Cause limited portions of content on the Website or the App to be displayed or appear to be displayed on your
          own or certain third-party websites.
        </p>
        <p>
          You may use these features solely as they are provided by us and solely with respect to the content they are
          displayed with and otherwise in accordance with any additional terms and conditions we provide with respect to
          such features. Subject to the foregoing, you must not:
        </p>
        <p>Establish a link from any website that is not owned by you.</p>
        <p>
          Cause Website or App or portions of it to be displayed on, or appear to be displayed by, any other site, for
          example, framing, deep linking, or in-line linking.
        </p>
        <p>Link to any part of Website or App other than the homepage.</p>
        <p>
          Otherwise take any action with respect to the materials on the Website or the App that is inconsistent with
          any other provision of these Terms of Use.
        </p>
        <p>
          The website from which you are linking, or on which you make certain content accessible, must comply in all
          respects with the Content Standards set out in these Terms of Use. You agree to cooperate with us in causing
          any unauthorized framing or linking immediately to stop. We reserve the right to withdraw linking permission
          without notice. We may disable all or any social media features and any links at any time without notice in
          our discretion.
        </p>
        <h3 className='font-bold text-lg'>Links from the Website or App</h3>
        <p>
          If Website or App contains links to other sites and resources provided by third parties, these links are
          provided for your convenience only. This includes links contained in advertisements, including banner
          advertisements and sponsored links. We have no control over the contents of those sites or resources and
          accept no responsibility for them or for any loss or damage that may arise from your use of them. If you
          decide to access any of the third-party websites linked to the Website or the App, you do so entirely at your
          own risk and subject to the terms and conditions of use for such websites.
        </p>
        <h3 className='font-bold text-lg'>Geographic Restrictions</h3>
        <p>
          The owner of Website and App is based in the state of Florida in the United States. We provide the Website and
          the App for use only by persons located in the United States. We make no claims that Website or App or any of
          its content is accessible or appropriate outside of the United States. Access to Website or App may not be
          legal by certain persons or in certain countries. If you access Website or App from outside the United States,
          you do so on your own initiative and are responsible for compliance with local laws.
        </p>
        <h3 className='font-bold text-lg'>Disclaimer of Warranties</h3>
        <p>
          You understand that we cannot and do not guarantee or warrant that files available for downloading from the
          internet or Website or App will be free of viruses or other destructive code. You are responsible for
          implementing sufficient procedures and checkpoints to satisfy your particular requirements for anti-virus
          protection and accuracy of data input and output, and for maintaining a means external to our site for any
          reconstruction of any lost data. TO THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE FOR ANY LOSS OR
          DAMAGE CAUSED BY A DISTRIBUTED DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY HARMFUL MATERIAL
          THAT MAY INFECT YOUR COMPUTER EQUIPMENT, COMPUTER PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE TO YOUR
          USE OF THE WEBSITE OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE OR TO YOUR DOWNLOADING OF ANY
          MATERIAL POSTED ON IT, OR ON ANY WEBSITE LINKED TO IT.
        </p>
        <p>
          YOUR USE OF THE WEBSITE, ITS CONTENT, AND ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE IS AT YOUR OWN
          RISK. THE WEBSITE, ITS CONTENT, AND ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE ARE PROVIDED ON AN “AS
          IS” AND “AS AVAILABLE” BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. NEITHER THE
          COMPANY NOR ANY PERSON ASSOCIATED WITH THE COMPANY MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE
          COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE WEBSITE. WITHOUT LIMITING THE
          FOREGOING, NEITHER THE COMPANY NOR ANYONE ASSOCIATED WITH THE COMPANY REPRESENTS OR WARRANTS THAT THE WEBSITE,
          ITS CONTENT, OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE WILL BE ACCURATE, RELIABLE, ERROR-FREE, OR
          UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT OUR SITE OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF
          VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT THE WEBSITE OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE
          WILL OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS.
        </p>
        <p>
          TO THE FULLEST EXTENT PROVIDED BY LAW, THE COMPANY HEREBY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER
          EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY,
          NON-INFRINGEMENT, AND FITNESS FOR PARTICULAR PURPOSE.
        </p>
        <p>THE FOREGOING DOES NOT AFFECT ANY WARRANTIES THAT CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.</p>
        <h3 className='font-bold text-lg'>Disclaimer of Medical or Health, Fitness or Nutritional Advice</h3>
        <p>IN ADDITION TO THE DISCLAIMER OF WARRANTIES CONTAINED ABOVE:</p>
        <p>
          THE WEBSITE AND THE APP, AND THE TOOLS MADE AVAILABLE THROUGH THE WEBSITE OR APP (INCLUDING INTERACTIVE CHAT
          FUNCTIONS) OFFER HEALTH, FITNESS AND NUTRITIONAL INFORMATION AND ARE DESIGNED FOR EDUCATIONAL PURPOSES ONLY.
          YOU SHOULD NOT RELY ON THIS INFORMATION AS A SUBSTITUTE FOR, NOR DOES IT REPLACE, PROFESSIONAL MEDICAL ADVICE,
          DIAGNOSIS, OR TREATMENT.&nbsp; ADDITIONALLY, YOU SHOULD NOT USE INFORMATION PROVIDED BY THE WEBSITE, THE APP,
          OR THE COMPANY AS A FORM OF BIRTH CONTROL OR RELY ON ANY SUCH INFORMATION TO PREVENT PREGNANCY.&nbsp; IF YOU
          HAVE ANY CONCERNS OR QUESTIONS ABOUT YOUR HEALTH, INCLUDING EFFECTIVE FORMS OF BIRTH CONTROL, YOU SHOULD
          ALWAYS CONSULT WITH A PHYSICIAN OR OTHER HEALTH-CARE PROFESSIONAL. DO NOT DISREGARD, AVOID OR DELAY OBTAINING
          MEDICAL OR HEALTH RELATED ADVICE FROM YOUR HEALTH-CARE PROFESSIONAL BECAUSE OF SOMETHING YOU MAY HAVE READ ON
          THIS SITE.&nbsp; SIMILARLY, DO NOT START A FITNESS PROGRAM CONTAINED ON THIS WEBSITE OR THE APP IF YOUR
          PHYSICIAN OR HEALTH CARE PROVIDER ADVISES AGAINST IT.&nbsp; AS WITH ANY EXERCISE PROGRAM, IF YOU EXPERIENCE
          FAINTNESS, DIZZINESS, PAIN, OR SHORTNESS OF BREATH AT ANY TIME WHILE EXERCISING YOU SHOULD STOP AND
          IMMEDIATELY CALL YOUR HEALTH CARE PROFESSIONAL OR 911. DEVELOPMENTS IN MEDICAL RESEARCH MAY IMPACT THE HEALTH,
          FITNESS AND NUTRITIONAL ADVICE THAT APPEARS HERE.&nbsp; NO ASSURANCE CAN BE GIVEN THAT THE ADVICE CONTAINED IN
          THE WEBSITE OR THE APP WILL ALWAYS INCLUDE THE MOST RECENT FINDINGS OR DEVELOPMENTS WITH RESPECT TO THE
          PARTICULAR MATERIAL.&nbsp; THE USE OF ANY INFORMATION PROVIDED ON THIS SITE IS SOLELY AT YOUR OWN RISK.&nbsp;
        </p>
        <h3 className='font-bold text-lg'>Limitation on Liability</h3>
        <p>
          TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL THE COMPANY, ITS AFFILIATES, OR THEIR LICENSORS,
          SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY
          LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE WEBSITE, THE APP, ANY
          WEBSITES LINKED TO IT, ANY CONTENT ON THE WEBSITE OR APP OR SUCH OTHER WEBSITES, INCLUDING ANY DIRECT,
          INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, PERSONAL
          INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR
          ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT (INCLUDING
          NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN IF FORESEEABLE. THE FOREGOING DOES NOT AFFECT ANY
          LIABILITY THAT CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.&nbsp;
        </p>
        <h3 className='font-bold text-lg'>Indemnification</h3>
        <p>
          You agree to defend, indemnify, and hold harmless the Company, its affiliates, licensors, and service
          providers, and its and their respective officers, directors, employees, contractors, agents, licensors,
          suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards,
          losses, costs, expenses, or fees (including reasonable attorneys’ fees) arising out of or relating to your
          violation of these Terms of Use or your use of Website or App, including, but not limited to, your User
          Contributions, any use of Website’s or App’s content, services, and products other than as expressly
          authorized in these Terms of Use or your use of any information obtained from Website or App.
        </p>
        <h3 className='font-bold text-lg'>Governing Law and Jurisdiction</h3>
        <p>
          All matters relating to Website or App and these Terms of Use, and any dispute or claim arising therefrom or
          related thereto (in each case, including non-contractual disputes or claims), shall be governed by and
          construed in accordance with the internal laws of the State of Florida without giving effect to any choice or
          conflict of law provision or rule (whether of the State of Florida or any other jurisdiction).
        </p>
        <p>
          Any legal suit, action, or proceeding arising out of, or related to, these Terms of Use or Website or App
          shall be instituted exclusively in the federal courts of the United States or the courts of the State of
          Florida, although we retain the right to bring any suit, action, or proceeding against you for breach of these
          Terms of Use in your country of residence or any other relevant country. You waive any and all objections to
          the exercise of jurisdiction over you by such courts and to venue in such courts.
        </p>
        <h3 className='font-bold text-lg'>Arbitration</h3>
        <p>
          At Company’s sole discretion, it may require You to submit any disputes arising from the use of these Terms of
          Use or Website or App, including disputes arising from or concerning their interpretation, violation,
          invalidity, non-performance, or termination, to final and binding arbitration under the Rules of Arbitration
          of the American Arbitration Association applying Florida law.
        </p>
        <h3 className='font-bold text-lg'>Limitation on Time to File Claims</h3>
        <p>
          ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR RELATING TO THESE TERMS OF USE OR THE WEBSITE OR
          APP MUST BE COMMENCED WITHIN ONE (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES, OTHERWISE, SUCH CAUSE OF ACTION
          OR CLAIM IS PERMANENTLY BARRED.
        </p>
        <h3 className='font-bold text-lg'>Waiver and Severability</h3>
        <p>
          No waiver by the Company of any term or condition set out in these Terms of Use shall be deemed a further or
          continuing waiver of such term or condition or a waiver of any other term or condition, and any failure of the
          Company to assert a right or provision under these Terms of Use shall not constitute a waiver of such right or
          provision.
        </p>
        <p>
          If any provision of these Terms of Use is held by a court or other tribunal of competent jurisdiction to be
          invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the
          minimum extent such that the remaining provisions of the Terms of Use will continue in full force and effect.
        </p>
        <h3 className='font-bold text-lg'>Entire Agreement</h3>
        <p>
          The Terms of Use<strong> </strong>and our<strong> </strong>
          <Link className='underline' href='/privacy'>
            Privacy Policy
          </Link>{' '}
          constitute the sole and entire agreement between you and 28 Wellness, Inc. regarding the Website and App and
          supersede all prior and contemporaneous understandings, agreements, representations, and warranties, both
          written and oral, regarding the Website and App.
        </p>
        <h3 className='font-bold text-lg'>Your Comments and Concerns</h3>
        This Website and App are operated by 28 Wellness, Inc. All notices of copyright infringement claims should be
        sent to the copyright agent designated in our Copyright Policy in the manner and by the means set out therein.
        All other feedback, comments, requests for technical support, and other communications relating to the Website
        or App should be directed to:{' '}
        <Link className='underline' href='mailto:support@28.co'>
          support@28.co
        </Link>
        .
      </div>
    </>;
};

export default TermsPage;